import { holdPropsInUtc } from "@repo/helpers";
import { ActivableFilterItem, FilterItem, KeyedArray, SerializedFile } from "@repo/models";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosRequestConfig } from "axios";
import { useCallback } from "react";

import { useRepContext } from "../contexts/editableItemContext";
import { useSettingsQuery } from "../helpers/queryHooks";
import { Rep } from "../models";
import { Machine } from "../models/machine";
import MapSale from "../models/mapSale";
import OnboardingSession from "../models/onboardingSession";
import { RepDeactivationDetails } from "../models/repDeactivationDetails";
import RepStatusButton from "../models/repStatusButton";
import { RepStatusHistory } from "../models/repStatusHistory";
import { SurveyRecord, SurveyResults } from "../models/surveys";
import { TabCounts } from "../models/tabCounts";
import * as Queries from "./queries";
import { AGENT_PORTAL_TAB_COUNTS } from "./queries";
import { useQueryWrapper, useRepPortalApi } from "./repPortalApi";

export function useRepApi() {
	const { saveRepDocument } = useRepDocumentApi();
	const { Get, Post } = useRepPortalApi();
	const [settings] = useSettingsQuery();

	const initializeRep = useCallback(
		(rep: Rep) => {
			holdPropsInUtc(
				rep,
				"startDate",
				"dateOfBirth",
				"deactivationDate",
				"effectiveDeactivationDate",
				"derivedDeactivationDate",
				"lastDisciplineEndDate",
				"lastDisciplineEndDate",
			);
			if (settings.timesInLocal) {
				holdPropsInUtc(
					rep,
					"onboardingDate",
					"lastMachinePingTimestamp",
					"dateLastSale",
					"firstSaleDate",
					"contingentExpirationDate",
				);
			}

			rep.channelId = rep.channelId || null;
			rep.marketerId = rep.marketerId || null;
			rep.stateId = rep.stateId || null;
			rep.branchId = rep.branchId || null;
			rep.managerId = rep.managerId || null;
			rep.showInactiveOffices = rep.branchIsInactive;
			rep.showInactiveManagers = rep.managerIsInactive;
			//Need to set start date here to ensure date is local
			rep.startDate = rep.repId ? rep.startDate : new Date();
			rep.attributes = rep.attributes ?? {};
			rep.mappedAttributes = { ...rep.attributes };
			for (const key of Object.keys(rep.attributes)) {
				rep.mappedAttributes[key] = rep.attributes[key];
				rep.mappedAttributes[key + "Id"] = rep.attributes[key];
				rep.mappedAttributes[key + "Name"] = rep.attributes[key];
			}
			return rep;
		},
		[settings.timesInLocal],
	);

	const checkIfLoginExists = useCallback(
		async function (rep: Rep) {
			const { login, repId, marketerId } = rep;
			if (!login) return false;
			return await Get<boolean>("LoginAlreadyExists", {
				login,
				repId,
				marketerId,
			});
		},
		[Get],
	);

	const checkForDuplicates = useCallback(
		async function (rep: Rep) {
			const { firstName, lastName, email, phone, lastFourSSN, branchId } = rep;
			return await Get<Rep[]>("CheckForDuplicates", {
				firstName,
				lastName,
				email,
				phone,
				lastFourSSN,
				branchId,
			});
		},
		[Get],
	);
	const saveRep = useCallback(
		function (rep: Rep) {
			const toSave = {
				...rep,
				photo: undefined,
				photoUrl: undefined,
				mappedAttributes: undefined,
				attributes: {},
			} as Rep;

			//Strip out dropdown stuff from attributes, and map back
			const mappedAttributes = { ...rep.mappedAttributes };
			const attributeKeys = Object.keys(mappedAttributes);
			for (const key of attributeKeys) {
				if (attributeKeys.includes(key + "Id")) delete mappedAttributes[key + "Id"];
				if (attributeKeys.includes(key + "Name")) delete mappedAttributes[key + "Name"];
			}
			toSave.attributes = mappedAttributes;

			return Post<number, any>("Save", null, toSave);
		},
		[Get],
	);

	const saveRepPhoto = useCallback(
		function (repId: number, photo: SerializedFile) {
			return saveRepDocument(repId, photo, 8);
		},
		[saveRepDocument],
	);

	const loadRep = useCallback(
		function (repId: number) {
			return Get<Rep>("GetRepresentativeInfo", { repId }).then(initializeRep);
		},
		[Get, initializeRep],
	);

	const getStatusButtons = useCallback(
		function (repId: number) {
			return Get<RepStatusButton[]>("GetStatusButtons", { repId });
		},
		[Get],
	);

	const getSegmentStatusButtons = useCallback(
		function (repSegmentId: number) {
			return Get<RepStatusButton[]>("GetSegmentStatusButtons", { repSegmentId });
		},
		[Get],
	);

	const getRelatedReps = useCallback(
		function (repId: number) {
			return Get<Rep[]>("GetSimilarReps", { repId });
		},
		[Get],
	);

	const getDisabledFeatures = useCallback(
		function (repId: number) {
			if (!repId) return null;
			return Get<Record<string, string>>("GetDisabledFeatures", { repId });
		},
		[Get],
	);

	const initializeRepStatusHistory = useCallback(
		(repStatusHistory: RepStatusHistory) => {
			if (settings.timesInLocal) {
				holdPropsInUtc(repStatusHistory, "createdOn");
			}
			return repStatusHistory;
		},
		[settings.timesInLocal],
	);

	const getStatusHistory = useCallback(
		async function (repId: number) {
			const statusHistory = await Get<RepStatusHistory[]>("GetRepresentativeStatusHistory", { repId });
			return statusHistory.map(initializeRepStatusHistory);
		},
		[Get, initializeRepStatusHistory],
	);

	const getSegmentStatusHistories = useCallback(
		async function (repSegmentId: number) {
			const segmentStatusHistories = await Get<RepStatusHistory[]>("GetSegmentStatusHistory", { repSegmentId });
			return segmentStatusHistories.map(initializeRepStatusHistory);
		},
		[Get, initializeRepStatusHistory],
	);

	const getDuplicateReps = useCallback(
		async function (repId: number) {
			return Get<Rep[]>("GetPotentialDuplicates", { repId });
		},
		[Get],
	);

	const getRecentEnrollments = useCallback(
		async function (repId: number, segmentId?: number) {
			return Get<{ enrollments: MapSale[]; lastMachinePing: MapSale }>("GetRecentEnrollments", {
				repId,
				segmentId,
			}).thenRun((d) => {
				if (d.lastMachinePing) d.lastMachinePing.enrollmentID = 0;
			});
		},
		[Get],
	);

	const getLanguages = useCallback(() => Get<FilterItem[]>("GetLanguages"), [Get]);
	const getRepTypes = useCallback(() => Get<FilterItem[]>("GetRepTypes"), [Get]);
	const getManagers = useCallback(
		(repId: number, branchId: number) => Get<ActivableFilterItem[]>("GetManagers", { repId, branchId }),
		[Get],
	);
	const getTrainers = useCallback(
		(repId: number, branchId: number) => Get<ActivableFilterItem[]>("GetTrainers", { repId, branchId }),
		[Get],
	);
	const getScreeningProviders = useCallback(
		(repId: number) => async () => Get<FilterItem[]>("GetScreeningProviders", { repId }),
		[Get],
	);

	const getOnboardingSessions = useCallback(
		async (repId: number) => {
			if (!repId) return null;
			const sessions = await Get<OnboardingSession[]>("GetOnboardingSessions", {
				repId,
			});
			return new KeyedArray(sessions, (k) => k.id);
		},
		[Get],
	);
	const updateRepData = useCallback((repId: number) => Get<boolean>("UpdateRepData", { repId }), [Get]);

	const updateStatus = useCallback(
		async function (statusName: string, repId?: number, repSegmentId?: number, details?: RepDeactivationDetails) {
			return await Post<number, RepDeactivationDetails>(statusName, { repId, repSegmentId }, details);
		},
		[Post],
	);

	const getActiveMachines = useCallback(
		async function (branchId: number) {
			if (!branchId) return null;
			return Get<Machine[]>("GetActiveMachines", { branchId });
		},
		[Get],
	);

	const getAssignedMachine = useCallback(
		async function (repLogin: string) {
			return Get<Machine>("GetAssignedMachine", { repLogin });
		},
		[Get],
	);

	const saveMachineAssignment = useCallback(
		async function (repLogin: string, machineId: number) {
			return Post<boolean>("SaveMachineAssignment", { repLogin, machineId });
		},
		[Post],
	);

	const updateOnboarding = useCallback(
		async function (repId: number) {
			return Post<boolean>("UpdateOnboarding", { repId });
		},
		[Post],
	);

	const getTabCounts = useCallback(
		async function (repId: number) {
			return Get<TabCounts>("GetTabCounts", { repId });
		},
		[Get],
	);

	const getSurveys = useCallback(
		async function (repId: number) {
			return Get<SurveyRecord[]>("GetSurveys", { repId });
		},
		[Get],
	);

	const getSurveyResults = useCallback(
		async function (repId: number) {
			return Get<SurveyResults>("GetSurveyResults", { repId });
		},
		[Get],
	);

	return {
		checkIfLoginExists,
		checkForDuplicates,
		saveRep,
		saveRepPhoto,
		loadRep,
		updateStatus,
		getLanguages,
		getRepTypes,
		getManagers,
		getTrainers,
		getScreeningProviders,
		updateRepData,
		getStatusButtons,
		getSegmentStatusButtons,
		getRelatedReps,
		getStatusHistory,
		getSegmentStatusHistories,
		getDuplicateReps,
		getRecentEnrollments,
		getOnboardingSessions,
		getActiveMachines,
		getAssignedMachine,
		saveMachineAssignment,
		updateOnboarding,
		getTabCounts,
		getSurveys,
		getSurveyResults,
		getDisabledFeatures,
	};
}

export function useRepQueries() {
	const {
		loadRep,
		getStatusButtons,
		getSegmentStatusButtons,
		getRelatedReps,
		getStatusHistory,
		getSegmentStatusHistories,
		getDuplicateReps,
		getRecentEnrollments,
		getOnboardingSessions,
		getDisabledFeatures,
	} = useRepApi();
	const { repId } = useRepContext();
	const [settings] = useSettingsQuery();

	const useRepInfoQuery = (setRepId?: number) =>
		useQueryWrapper(Queries.REP_DETAILS, [setRepId ?? repId], () => loadRep(setRepId ?? repId), {
			staleTime: Infinity,
		});

	const useStatusButtonsQuery = (repId: number) =>
		useQueryWrapper(Queries.REP_STATUS_BUTTONS, [repId], () => getStatusButtons(repId));
	const useSegmentStatusButtonsQuery = (repSegmentId: number) =>
		useQueryWrapper(Queries.REP_SEGMENT_STATUS_BUTTONS, [repSegmentId], () =>
			getSegmentStatusButtons(repSegmentId),
		);

	const useRelatedRepsQuery = () => useQueryWrapper(Queries.REP_RELATED_REPS, [repId], () => getRelatedReps(repId));
	const useStatusHistoryQuery = () =>
		useQueryWrapper(Queries.REP_STATUS_HISTORIES, [repId, settings.timesInLocal], () => getStatusHistory(repId));
	const useSegmentStatusHistoryQuery = (repSegmentId: number) =>
		useQueryWrapper(
			Queries.REP_STATUS_HISTORIES,
			[repSegmentId, settings.timesInLocal],
			() => getSegmentStatusHistories(repSegmentId),
			{ enabled: !!repSegmentId },
		);
	const useDuplicateRepsQuery = () =>
		useQueryWrapper(Queries.REP_POT_DUPLICATES, [repId], () => getDuplicateReps(repId));
	const useRecentEnrollmentsQuery = (repSegmentId?: number) =>
		useQueryWrapper(Queries.REP_ENROLLMENTS, [repId, repSegmentId], () =>
			getRecentEnrollments(repId, repSegmentId),
		);
	const useOnboardingSessionsQuery = () =>
		useQueryWrapper(Queries.ONBOARDING_SESSIONS, [repId], () => getOnboardingSessions(repId));
	const useDisabledFeaturesQuery = () =>
		useQueryWrapper(Queries.REP_DISABLED_FEATURES, [repId], () => getDisabledFeatures(repId));
	return {
		useRepInfoQuery,
		useStatusButtonsQuery,
		useSegmentStatusButtonsQuery,
		useRelatedRepsQuery,
		useStatusHistoryQuery,
		useSegmentStatusHistoryQuery,
		useDuplicateRepsQuery,
		useRecentEnrollmentsQuery,
		useOnboardingSessionsQuery,
		useDisabledFeaturesQuery,
	};
}

export function useRepOnboardingQuery() {
	const { repId } = useRepContext();
	const queryClient = useQueryClient();
	const { updateOnboarding } = useRepApi();
	const checkOnboarding = useCallback(async () => {
		const isUpdated = await updateOnboarding(repId);
		//MSC (5/7/24): useQuery doesn't do invalidateQuery(key) where the key we pass in isn't the first key in the array
		//So in order to invalidate a group, we need to do a .includes
		const toInvalidate = isUpdated ? Queries.GROUP.REP : Queries.ONBOARDING_REQUIREMENTS_KEY;
		await queryClient.invalidateQueries({
			predicate: (q) => q.queryKey.includes(toInvalidate),
		});
		return isUpdated;
	}, [repId]);

	return { checkOnboarding };
}

export function useRepDocumentApi() {
	const { Get, Post } = useRepPortalApi();

	const saveRepDocument = useCallback(
		async function (
			repId: number,
			file: SerializedFile,
			docType: RepresentativeDocumentTypes,
			backgroundCheckId: number = null,
			drugTestId: number = null,
			config: AxiosRequestConfig = {},
		) {
			if (!file) return null;
			const data = new FormData();
			data.append("file", file.url);
			data.append("fileName", file.name);
			data.append("fileType", file.type);
			config.headers = {
				...config.headers,
				"Content-Type": "application/x-www-form-urlencoded",
			};
			return await Post<number, any>(
				"SaveDocument",
				{ repId, docType, backgroundCheckId, drugTestId },
				data,
				config,
			);
		},
		[Post],
	);

	const deleteRepDocument = useCallback(
		function (fileId: number, config?: AxiosRequestConfig) {
			return Post<number, any>(`DeleteDocument`, { fileId }, {}, config);
		},
		[Post],
	);

	const getRepDocument = useCallback(
		function (fileId: number, config?: AxiosRequestConfig) {
			return Get<any>(`GetRepDocument/${fileId}`, {}, config);
		},
		[Get],
	);

	return { saveRepDocument, deleteRepDocument, getRepDocument };
}

export function useRepMachineApi() {
	const { getActiveMachines, getAssignedMachine } = useRepApi();
	const { rep } = useRepContext();

	const useActiveMachinesQuery = () =>
		useQueryWrapper(Queries.REP_MACHINES_ACTIVE, [rep.branchId], () => getActiveMachines(rep.branchId));

	const useAssignedMachineQuery = () =>
		useQueryWrapper(Queries.REP_MACHINES_ASSIGNED, [rep.login], async () => {
			if (!rep.repId) return null;
			return await getAssignedMachine(rep.login);
		});

	return { useActiveMachinesQuery, useAssignedMachineQuery };
}

export function useTabCountQuery(repId: number) {
	const { getTabCounts } = useRepApi();

	return useQueryWrapper(AGENT_PORTAL_TAB_COUNTS, [repId], () => getTabCounts(repId), { enabled: !!repId });
}

export const useSurveyQuery = () => {
	const { repId } = useRepContext();
	const { getSurveys } = useRepApi();
	return useQueryWrapper(Queries.REP_SURVEYS, [repId], () => getSurveys(repId));
};

export enum BackgroundCheckDocumentTypes {
	BackgroundCheckConsentDocument = 1,
	BackgroundCheckResultDocument = 2,
	DriversLicenseDocumentFront = 7,
	DriversLicenseDocumentBack = 11,
	ExceptionDocument = 43,
}
export enum DrugTestDocumentTypes {
	DrugTestConsentDocument = 3,
	DrugTestResultDocument = 4,
	DrugTestResultSecondDocument = 5,
}
export enum RepresentativeDocumentGeneralTypes {
	Other = 6,

	Photo = 8,
	PerformanceEthicalStandardsDocument = 9,
	CovidVaccination = 10,

	IncidentDocument = 31,

	Dynamic = 100,
}

export type RepresentativeDocumentTypes =
	| BackgroundCheckDocumentTypes
	| DrugTestDocumentTypes
	| RepresentativeDocumentGeneralTypes;
